import { Input } from '@/shared/components/ui/Input';
import { Card } from '@/shared/components/ui/Card';
import { ImagePlus, MapPin, Edit3, X } from 'lucide-react';
import { useState } from 'react';
import type { OrderState } from '../../pages/CreateSalesOrderPage';

interface Props {
  state: OrderState;
  updateState: (updater: (prev: OrderState) => OrderState) => void;
}

export function OrderHeaderForm({ state, updateState }: Props) {
  const [isEditingSender, setIsEditingSender] = useState(false);

  const addCustomField = () => {
    updateState(s => ({
      ...s,
      headerCustomFields: [...(s.headerCustomFields || []), { id: Math.random().toString(), name: '', value: '' }]
    }));
  };

  const updateCustomField = (index: number, key: 'name' | 'value', val: string) => {
    updateState(s => {
      const newFields = [...(s.headerCustomFields || [])];
      newFields[index] = { ...newFields[index], [key]: val };
      return { ...s, headerCustomFields: newFields };
    });
  };

  const removeCustomField = (index: number) => {
    updateState(s => {
      const newFields = [...(s.headerCustomFields || [])];
      newFields.splice(index, 1);
      return { ...s, headerCustomFields: newFields };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="flex-1 space-y-4">
          <Input
            label="Order No *"
            value={state.orderNo}
            onChange={(e) => updateState(s => ({ ...s, orderNo: e.target.value }))}
          />
          <Input
            label="Sales Order Date *"
            type="date"
            value={state.date}
            onChange={(e) => updateState(s => ({ ...s, date: e.target.value }))}
          />

          {(state.headerCustomFields || []).length > 0 && (
            <div className="space-y-3 pt-2">
              {(state.headerCustomFields || []).map((field, idx) => (
                <div key={field.id} className="flex items-end gap-2">
                  <Input aria-label="Header field name" className="w-40" placeholder="Field Name" value={field.name} onChange={(e) => updateCustomField(idx, 'name', e.target.value)} />
                  <Input aria-label="Header field value" className="flex-1" placeholder="Value" value={field.value} onChange={(e) => updateCustomField(idx, 'value', e.target.value)} />
                  <button onClick={() => removeCustomField(idx)} aria-label="Remove header field" className="mb-0.5 rounded p-1.5 text-on-surface-subtle transition-colors hover:bg-destructive/10 hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}

          <button onClick={addCustomField} className="mt-2 flex items-center gap-1 text-body-sm font-medium text-primary hover:underline">
            <span className="text-lg leading-none">+</span> Add Custom Fields
          </button>
        </div>

        <div className="flex w-48 shrink-0 flex-col items-center justify-center rounded-card-sm border-2 border-dashed border-outline-variant bg-muted/50 p-4 text-center transition-colors hover:bg-muted">
          <ImagePlus className="mb-2 h-6 w-6 text-primary" />
          <span className="text-body-sm font-medium text-on-surface">Add Business Logo</span>
          <span className="mt-1 text-body-xs text-on-surface-variant">Resolution up to 1080x1080px.<br />PNG or JPEG file.</span>
        </div>
      </div>

      <Card className="border-border bg-card p-4 shadow-xs">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-label-sm uppercase tracking-wider text-on-surface-variant">
            Delivered By <span className="text-body-xs font-normal normal-case tracking-normal text-on-surface-subtle">(Your details)</span>
          </h3>
        </div>
        <div className="rounded-card-sm border border-border bg-surface p-3">
          {isEditingSender ? (
            <div className="space-y-3">
              <Input
                label="Name"
                value={state.deliveredBy.name}
                onChange={(e) => updateState(s => ({ ...s, deliveredBy: { ...s.deliveredBy, name: e.target.value } }))}
              />
              <div>
                <label htmlFor="so-sender-country" className="mb-1.5 block text-body-sm font-medium text-on-surface">Country</label>
                <select
                  id="so-sender-country"
                  className="h-10 w-full rounded-lg border border-input bg-surface px-3 text-data-md text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={state.deliveredBy.country}
                  onChange={(e) => updateState(s => ({ ...s, deliveredBy: { ...s.deliveredBy, country: e.target.value } }))}
                >
                  <option value="India">India</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                </select>
              </div>
              <div className="flex justify-end mt-2">
                <button onClick={() => setIsEditingSender(false)} className="text-body-sm font-medium text-primary hover:underline">Done</button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-data-md font-medium text-on-surface">
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-inverse-surface text-data-xs font-semibold text-inverse-on-surface">
                    {state.deliveredBy.name.charAt(0).toUpperCase() || 'S'}
                  </span>
                  {state.deliveredBy.name || <span className="text-on-surface-subtle italic">Not set</span>}
                </div>
                <div className="mt-2 flex items-center gap-1 text-body-sm text-on-surface-variant">
                  <MapPin className="h-3 w-3" /> {state.deliveredBy.country}
                </div>
              </div>
              <button onClick={() => setIsEditingSender(true)} className="flex items-center gap-1 text-body-sm font-medium text-primary hover:underline">
                <Edit3 className="h-3 w-3" /> Edit
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
